import React from 'react';
import ChatThread from './ChatThread';

/**
 * ChatPanel — the branded chat surface (Widget v2, plan W3): header (icon,
 * display name, optional background image, close button in float mode),
 * ChatThread, footer (terms link + powered-by). Used by FloatingLauncher and,
 * without the close button, as the inline card body and admin live preview.
 */

export function LauncherIcon({ icon, size = 22, color = '#fff' }) {
  if (icon && /^https:\/\//.test(icon)) {
    return <img src={icon} alt="" style={{ width: size, height: size, objectFit: 'contain' }} />;
  }
  const common = { width: size, height: size, fill: 'none', stroke: color,
                   strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (icon === 'question') {
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  if (icon === 'sparkle') {
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
        <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z" />
      </svg>
    );
  }
  // default: chat bubble
  return (
    <svg viewBox="0 0 24 24" {...common}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function ChatPanel({
  backendUrl, siteId, signedToken, channelUrl, customerKey,
  agencyName, tracker, c, config = {},
  onClose = null, showHeader = true, trackOpenOnFocus = true,
  panelId = undefined, style = {},
}) {
  const threadRef = React.useRef(null);
  const [audience, setAudience] = React.useState(null);
  const displayName = config.display_name || agencyName;
  const headerStyle = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px',
    background: c.headerBg || c.accent, color: c.headerText || '#fff',
    borderRadius: '12px 12px 0 0', flexShrink: 0,
  };
  if (config.header_image_url) {
    headerStyle.backgroundImage =
      `linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.35)), url("${config.header_image_url}")`;
    headerStyle.backgroundSize = 'cover';
    headerStyle.backgroundPosition = 'center';
    headerStyle.color = '#fff';
  }

  return (
    <div
      id={panelId}
      className="inquiros-chat inquiros-panel"
      style={{
        display: 'flex', flexDirection: 'column',
        background: c.bg, color: c.text, fontFamily: c.font,
        borderRadius: 12, overflow: 'hidden', boxSizing: 'border-box',
        ...style,
      }}
    >
      {showHeader && (
        <div style={headerStyle}>
          <LauncherIcon icon={config.launcher_icon} size={20}
                        color={headerStyle.color} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>{displayName}</div>
            {config.header_text ? (
              <div style={{ fontSize: 12, opacity: 0.85 }}>{config.header_text}</div>
            ) : null}
          </div>
          {audience && (
            <button type="button" aria-label={`Change audience (${audience.label})`}
              onClick={() => threadRef.current?.changeAudience()}
              style={{ borderRadius: 999, padding: '5px 8px', fontSize: 12, cursor: 'pointer',
                border: '1px solid currentColor', background: 'transparent', color: 'inherit' }}>
              {audience.label} · Change
            </button>
          )}
          {onClose && (
            // Minimize, not close: the launcher stays on the page and the
            // conversation is kept, so the visitor can always come back.
            <button
              type="button" onClick={onClose} aria-label="Minimize chat"
              title="Minimize. Your conversation stays here."
              style={{
                marginLeft: 'auto', background: 'rgba(255,255,255,.15)', color: 'inherit',
                border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0,
              }}
            >
              <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor"
                   strokeWidth={2.4} strokeLinecap="round" aria-hidden="true">
                <line x1="6" y1="12" x2="18" y2="12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* The bot display_name brands the header; the agency name owns all
          "…'s published materials" copy and the /api/ask prompt — they are
          different things (bot: "Ask Edwards", agency: "Edwards Aquifer"). */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '10px 14px 12px' }}>
        <ChatThread
          key={siteId || channelUrl || 'default'} ref={threadRef}
          audiences={config.audiences} audienceGate={!!config.audience_gate}
          audiencePrompt={config.audience_prompt || 'Which best describes you?'}
          onAudienceChange={setAudience}
          backendUrl={backendUrl} siteId={siteId} signedToken={signedToken}
          channelUrl={channelUrl} customerKey={customerKey}
          agencyName={agencyName} tracker={tracker} c={c}
          starterQuestions={config.starter_questions || []}
          answerDisclaimer={config.answer_disclaimer || ''}
          inputPlaceholder={config.input_placeholder || ''}
          sourcesDisplay={config.sources_display || 'collapsed'}
          trackOpenOnFocus={trackOpenOnFocus}
        />
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: c.muted, lineHeight: 1.45 }}>
          Answers come only from {agencyName}'s published materials.
          {config.terms_url ? (
            <>
              {' '}
              <a href={config.terms_url} target="_blank" rel="noopener noreferrer"
                 style={{ color: c.muted, textDecoration: 'underline' }}>
                {config.terms_label || 'Terms of use'}
              </a>
            </>
          ) : null}
          <span style={{ float: 'right', opacity: 0.8 }}>Powered by Inquiros</span>
        </p>
      </div>
    </div>
  );
}

export default ChatPanel;
